import React, { ReactElement, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useModal from './useModal';
import { useApp } from '../contexts/AppContext';
import * as Sentry from '@sentry/react';
import styled from '@emotion/styled';
import Container from '../components/blocks/Container';
import store from '../store';
import { handleProfileCompletionVysionEvent } from '../utils/vysion/profileCompletion';
import { VYSION_ACTIONS } from '../utils/vysion';
import { ProfileCompletionViewVysionEventsEnum } from '../utils/vysion/profileCompletion/view';
import { ProfileCompletionClickVysionEventsEnum } from '../utils/vysion/profileCompletion/click';
import { EVENT_NAMES } from '../constants/mixpanel';
import CompleteProfileWall from '../components/completeProfile/CompleteProfileWall';
import useMediaBreakpoints from './useMediaBreakpoints';
import { mixpanelTrackEvent } from '../utils/mixpanel/common';
import breakpoints from 'theme/material/breakpoints';

const StyledContainer = styled(Container)({
  [breakpoints.up(769)]: {
    padding: '0',
  },
});

interface IUseCompleteProfileModal {
  showMandatoryCompleteProfile(
    callback?: () => void,
    callbackAfterValidate?: () => void,
    action?: string,
    shouldForceRunCallback?: boolean
  ): void;
}

interface IModalBodyProps {
  action: string;
}

const ModalBody = ({ action }: IModalBodyProps): ReactElement => {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname) {
      handleProfileCompletionVysionEvent(
        {
          action: VYSION_ACTIONS.VIEW,
          event: ProfileCompletionViewVysionEventsEnum.VIEW_FINISH_MY_PROFILE,
        },
        location.pathname
      );
    }
  }, [location.pathname]);

  return <CompleteProfileWall action={action} />;
};

const useCompleteProfileModal = (): IUseCompleteProfileModal => {
  const { basicModal } = useModal();
  const { setProfileCompletionCallback, setProfileCompletionCallbackAfterValidate } = useApp();
  const { isMobile } = useMediaBreakpoints();

  const location = useLocation();

  const handlePopupVysionOnClose = () => {
    handleProfileCompletionVysionEvent(
      {
        action: VYSION_ACTIONS.CLICK,
        event: ProfileCompletionClickVysionEventsEnum.CLICK_FINISH_PROFILE_CLOSE,
      },
      location.pathname
    );
  };

  const showMandatoryCompleteProfile = (
    callback = () => undefined,
    callbackAfterValidate = () => undefined,
    action = '',
    shouldForceRunCallback = false
  ) => {
    try {
      const profileState = store.getState()?.profile ?? null;

      if (profileState === null) {
        return;
      }

      const profileValidation = profileState.mandatory_profile_status;
      const isProfileComplete = profileState.mandatory_profile_completed;

      if (isProfileComplete || shouldForceRunCallback) {
        callback();
        return;
      }

      setProfileCompletionCallback(() => callback);
      setProfileCompletionCallbackAfterValidate(() => callbackAfterValidate);

      mixpanelTrackEvent({
        eventName: EVENT_NAMES.PROFILE_WALL_STEP_1_SHOWN,
        payload: {
          eventProperties: {
            Action: action,
            'Fields Missed': profileValidation?.fields_missed ?? [],
          },
        },
      });

      basicModal({
        dateTestId: 'complete-profile-reminder',
        modalContentLabel: 'Complete Profile Reminder',
        isModalOpen: true,
        withoutBgPattern: true,
        closeOnOverlayClick: false,
        modalBody: (
          <StyledContainer>
            <ModalBody action={action} />
          </StyledContainer>
        ),
        customContentStyle: { maxWidth: isMobile ? 420 : 500 },
        onCloseClickHandler: () => {
          handlePopupVysionOnClose();
          callbackAfterValidate();
        },
        enableTouchMove: true,
      });
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'useCompleteProfileModal', action: 'showMandatoryCompleteProfile' },
      });
    }
  };

  return {
    showMandatoryCompleteProfile,
  };
};

export default useCompleteProfileModal;
